import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { initializeAOS, refreshAOS } from "./utils/aosInit";
import { useFirebaseDataReady } from "./hooks/useFirebaseDataReady";
import automaticSyncService from "./services/automaticSyncService";
import { clearAllListeners } from "./firebase/firestore";
import { initializeHomepageSync, cleanupHomepageSync } from "./services/homepageSyncService";
import { ensureStorageInitialized } from "./firebase/config-v9";

import "./App.css";
import { AppProviders } from "./components/AppProviders";
import { useCart } from "./contexts/CartContext";
import { useHomepageData } from "./hooks/useHomepageData";
import Navbar from "./components/Navbar.jsx";
import Footer from "./components/Footer.jsx";
import Collections from "./components/Collections.jsx";
import Occasion from "./components/OccasionEdit.jsx";
import VarahaLoading from "./pages/VarahaLoading.jsx";
import GlobalAppLoader from "./components/GlobalAppLoader.jsx";
import Login from "./components/Login.jsx";
import PopupWrapper from "./components/PopupWrapper.jsx";
import AdminGuard from "./components/AdminGuard.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import SkeletonLoader from "./components/SkeletonLoader.jsx";
// Home page should NOT be lazy-loaded - it's above the fold and critical for first paint
import Home from "./pages/Home.jsx";

// Lazy load heavy components for better performance
const ShopListCategory = lazy(() => import("./components/ShopListCategory.jsx"));
const ProductDetail = lazy(() => import("./pages/ProductDetail.jsx"));
const ShopListWedding = lazy(() => import("./components/ShopListWedding.jsx"));
const ShopListEngagement = lazy(() => import("./components/ShopListEngagement.jsx"));
const ShopListHaldi = lazy(() => import("./components/ShopListHaldi.jsx"));
const ShopListPoojaRitual = lazy(() => import("./components/ShopListPoojaRitual.jsx"));
const ShopListMehendi = lazy(() => import("./components/ShopListMehendi.jsx"));
const ShopListFestival = lazy(() => import("./components/ShopListFestival.jsx"));
const ShopListSareeFunction = lazy(() => import("./components/ShopListSareeFunction.jsx"));
const WhatsAppIcon = lazy(() => import("./components/WhatsAppIcon.jsx"));

// WhatsApp component with delay to avoid blocking initial render
const LazyWhatsAppWithDelay = () => {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    // Delay WhatsApp widget loading by 1 second to prioritize main content
    const timer = setTimeout(() => {
      setShow(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (!show) return null;
  
  return <WhatsAppIcon />;
};

// Lazy load other pages for code splitting
const About = lazy(() => import("./pages/About.jsx"));
const Account = lazy(() => import("./pages/Account.jsx"));
const Cart = lazy(() => import("./pages/Cart.jsx"));
const Checkout = lazy(() => import("./pages/Checkout.jsx"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess.jsx"));
const Wishlist = lazy(() => import("./pages/Wishlist.jsx"));
const Orders = lazy(() => import("./pages/Orders.jsx"));
const Contact = lazy(() => import("./pages/Contact.jsx"));
const OrderHistory = lazy(() => import("./pages/OrderHistory.jsx"));

// Lazy load admin components (only loaded when admin routes are accessed)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.jsx"));
const AdminLogin = lazy(() => import("./pages/AdminLogin.jsx"));
const CategoryManagement = lazy(() => import("./components/admin/CategoryManagement.jsx"));
const ProductManager = lazy(() => import("./components/ProductManager.jsx"));

// Page skeleton for route-level loading
const PageSkeleton = () => (
  <div style={{ minHeight: '60vh', padding: '2rem' }}>
    <SkeletonLoader.Box width="100%" height="300px" className="skeleton-margin-bottom" />
    <SkeletonLoader.Text lines={3} width="100%" />
    <SkeletonLoader.Grid count={4} />
  </div>
);

// Component that uses cart context
function AppContent() {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  // Handler for Buy Now - adds to cart and navigates to checkout
  const handleBuyNow = async (product) => {
    try {
      await addToCart(product);
      navigate('/checkout');
    } catch (error) {
      console.error('Error adding to cart for buy now:', error);
      // Still navigate to checkout even if there's an error
      navigate('/checkout');
    }
  };
  
  const CollectionsPage = () => {
    const {
      products,
      categories,
      loading,
    } = useHomepageData();

    return (
      <Collections
        categories={categories}
        products={products}
        loading={loading}
        onViewAllKanjivaram={() => navigate("/shop/kanchipuram")}
        onViewAllBanarasi={() => navigate("/shop/venkatagiri")}
        onViewAllTussar={() => navigate("/shop/mysore")}
        onViewAllPatola={() => navigate("/shop/kalamkari")}
        onViewAllLinen={() => navigate("/shop/kerala")}
        onViewAllWedding={() => navigate("/shop/wedding")}
        onViewAllEngagement={() => navigate("/shop/engagement")}
        onViewAllHaldi={() => navigate("/shop/haldi")}
        onViewAllMehendi={() => navigate("/shop/mehendi")}
        onViewAllBabyShower={() => navigate("/shop/babyshower")}
        onViewAllPoojaRitual={() => navigate("/shop/poojaritual")}
        onViewAllFestival={() => navigate("/shop/festival")}
        onViewAllSareeFunction={() => navigate("/shop/sareefunction")}
        onExploreFilters={() => navigate("/shop/kanchipuram")}
        onChatWithStylist={() => navigate("/contact")}
        onSelectCollection={(route) => navigate(route)}
      />
    );
  };

  return (
    <Routes>
      <Route path="/" element={<Home />} />

      <Route
        path="/collections"
        element={
          <CollectionsPage />
        }
      />

      {/* Dynamic product detail route for any category */}
      <Route path="/product/:categoryId" element={
        <Suspense fallback={<PageSkeleton />}>
          <ProductDetail />
        </Suspense>
      } />
      <Route path="/product" element={
        <Suspense fallback={<PageSkeleton />}>
          <ProductDetail />
        </Suspense>
      } />
      {/** Dynamic category route for any category created in dashboard */}
      <Route path="/shop/:categoryId" element={
        <Suspense fallback={<PageSkeleton />}>
          <ShopListCategory />
        </Suspense>
      } />
      <Route
        path="/shop/wedding"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListWedding
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />
      <Route
        path="/shop/engagement"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListEngagement
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />
      <Route
        path="/shop/haldi"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListHaldi
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />
      <Route
        path="/shop/pooja-ritual"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListPoojaRitual
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />
      <Route
        path="/shop/mehendi"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListMehendi
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />
      <Route
        path="/shop/festival"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListFestival
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />
      <Route
        path="/shop/saree-function"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ShopListSareeFunction
              onBack={() => navigate("/collections")}
              onAddToBag={handleBuyNow}
            />
          </Suspense>
        }
      />

      <Route path="/about" element={
        <Suspense fallback={<PageSkeleton />}>
          <About />
        </Suspense>
      } />
      <Route path="/account" element={
        <Suspense fallback={<PageSkeleton />}>
          <Account />
        </Suspense>
      } />
      <Route path="/cart" element={
        <Suspense fallback={<PageSkeleton />}>
          <Cart />
        </Suspense>
      } />
      <Route path="/orders" element={
        <Suspense fallback={<PageSkeleton />}>
          <Orders />
        </Suspense>
      } />
      <Route path="/order-history" element={
        <Suspense fallback={<PageSkeleton />}>
          <OrderHistory />
        </Suspense>
      } />
      <Route path="/checkout" element={
        <Suspense fallback={<PageSkeleton />}>
          <Checkout />
        </Suspense>
      } />
      <Route path="/payment-success" element={
        <Suspense fallback={<PageSkeleton />}>
          <PaymentSuccess />
        </Suspense>
      } />
      <Route path="/wishlist" element={
        <Suspense fallback={<PageSkeleton />}>
          <Wishlist />
        </Suspense>
      } />
      <Route
        path="/occasion"
        element={
          <Occasion
            onNavigateToCollections={() => navigate("/collections")}
          />
        }
      />
      <Route path="/contact" element={
        <Suspense fallback={<PageSkeleton />}>
          <Contact />
        </Suspense>
      } />
      <Route path="/admin/products" element={
        <Suspense fallback={<PageSkeleton />}>
          <ProductManager />
        </Suspense>
      } />
      <Route path="/admin/categories" element={
        <AdminGuard>
          <Suspense fallback={<PageSkeleton />}>
            <CategoryManagement />
          </Suspense>
        </AdminGuard>
      } />
      <Route path="/admin/login" element={
        <Suspense fallback={<PageSkeleton />}>
          <AdminLogin />
        </Suspense>
      } />
      <Route path="/admin" element={
        <AdminGuard>
          <Suspense fallback={<PageSkeleton />}>
            <AdminDashboard />
          </Suspense>
        </AdminGuard>
      } />
    </Routes>
  );
}

export default function App({ onMount }) {
  const { pathname } = useLocation();
  const { isReady: firebaseDataReady } = useFirebaseDataReady();
  
  // App ready state - waits for Firestore initialization and cache hydration
  const [appReady, setAppReady] = useState(false);
  
  // Remove splash screen on mount
  useEffect(() => {
    if (onMount) {
      onMount();
    } else {
      // Fallback: remove splash directly
      const splash = document.getElementById('vs-splash');
      if (splash) splash.remove();
    }
  }, [onMount]);
  
  // Check if this is the first visit (not a refresh)
  const isFirstVisit = !sessionStorage.getItem('varaha_visited');
  const [showLoader, setShowLoader] = useState(isFirstVisit); // Only show on first visit
  const isInitialMount = useRef(true);
  const previousPathname = useRef(pathname);
  const loaderStarted = useRef(false); // Track if loader logic has started
  const firebaseDataReadyRef = useRef(firebaseDataReady); // Ref to track latest firebaseDataReady value
  const timeoutIdRef = useRef(null); // Ref to store timeout ID across effect runs
  
  // Render UI immediately - don't wait for Firestore
  // Firestore will load in background and update UI when ready
  useEffect(() => {
    // Set app ready immediately (non-blocking)
    // Firestore initialization happens in background
    const timer = setTimeout(() => {
      setAppReady(true);
    }, 50); // Minimal delay just to ensure React has rendered
    return () => clearTimeout(timer);
  }, []); // Run once on mount
  
  // Mark as visited on first load
  useEffect(() => {
    if (isFirstVisit) {
      sessionStorage.setItem('varaha_visited', 'true');
    }
  }, [isFirstVisit]);
  
  // Update ref whenever firebaseDataReady changes
  useEffect(() => {
    firebaseDataReadyRef.current = firebaseDataReady;
  }, [firebaseDataReady]);

  // Register service worker for production builds
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.warn('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Defer Firebase initialization - only load when actually needed
  // This prevents blocking first paint for new users
  useEffect(() => {
    // Only run on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      // Defer Firebase initialization using requestIdleCallback
      // Firebase will only load when needed (for authenticated features)
      const initFirebaseLazy = async () => {
        // Use requestIdleCallback if available, otherwise setTimeout
        const scheduleInit = (callback) => {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(callback, { timeout: 5000 });
          } else {
            setTimeout(callback, 2000);
          }
        };

        scheduleInit(async () => {
          try {
            // Dynamically import Firebase only when needed
            const { initializeFirebaseLazy } = await import('./firebase/config-lazy');
            await initializeFirebaseLazy();
            
            // Initialize homepage sync service after Firebase is ready
            initializeHomepageSync();
          } catch (error) {
            console.warn('Lazy Firebase initialization failed (non-critical):', error);
            // Continue without Firebase - API layer will handle public data
          }
        });
      };

      initFirebaseLazy();
    }
    
    // Cleanup on unmount
    return () => {
      cleanupHomepageSync();
    };
  }, []); // Empty dependency array - only runs on mount

  useEffect(() => {
    ensureStorageInitialized().catch((error) => {
      console.warn('Storage initialization failed (non-critical):', error);
    });
  }, []);

  useEffect(() => {
    initializeAOS({ duration: 800, once: true, offset: 0, easing: "ease-in-out" });
    
    // Update body background color based on loader state
    if (pathname.startsWith("/admin")) {
      document.body.style.backgroundColor = "#fff";
      setShowLoader(false);
      loaderStarted.current = false; // Reset for admin routes
      // Clear any existing timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    } else {
      // Check if this is a route change (not initial mount)
      const isRouteChange = previousPathname.current !== pathname && !isInitialMount.current;
      previousPathname.current = pathname;
      
      // For route changes, NEVER show loader
      if (isRouteChange) {
        // Route change - hide loader immediately, don't show it
        setShowLoader(false);
        loaderStarted.current = false;
        // Clear any existing timeout
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      } else {
        // Initial mount - only show loader on first visit (not on refresh)
        if (isFirstVisit && !loaderStarted.current) {
          loaderStarted.current = true; // Mark that we've started
          // CRITICAL FIX: Very aggressive times for first-time users - show content ASAP
          const minLoaderTime = 100; // Minimal time for smooth transition (100ms)
          const maxLoaderTime = 300; // Maximum 300ms - very aggressive for first-time users
          const startTime = Date.now();
          
          const checkReady = () => {
            const elapsed = Date.now() - startTime;
            const minTimeElapsed = elapsed >= minLoaderTime;
            const maxTimeElapsed = elapsed >= maxLoaderTime;
            const isReady = firebaseDataReadyRef.current; // Use ref to get latest value
            
            // Hide loader if:
            // 1. Firebase data is ready AND minimum time elapsed, OR
            // 2. Maximum time elapsed (force hide even if data not ready) - CRITICAL for new users
            if ((isReady && minTimeElapsed) || maxTimeElapsed) {
              setShowLoader(false);
              loaderStarted.current = false; // Reset after hiding
              timeoutIdRef.current = null;
            } else {
              // Check again in 25ms for faster response (reduced from 50ms)
              timeoutIdRef.current = setTimeout(checkReady, 25);
            }
          };
          
          // Start checking immediately
          checkReady();
        } else if (!isFirstVisit) {
          // Not first visit (refresh) - hide loader immediately and show content right away
          setShowLoader(false);
          loaderStarted.current = false;
          // Set body background to white immediately on refresh
          if (!pathname.startsWith("/admin")) {
            document.body.style.backgroundColor = "#fff";
          }
        }
        // If loader already started, don't restart it even if firebaseDataReady changes
      }
    }
    
    // Start automatic Shiprocket sync service
    automaticSyncService.start();
    
    return () => {
      // Don't clear timeout here - let it continue running
      // Only clear on route change or admin route
      automaticSyncService.stop();
    };
  }, [pathname, firebaseDataReady, isFirstVisit]);

  // Cleanup Firestore listeners when app unmounts
  useEffect(() => {
    return () => {
      // Use static import to avoid HMR issues with dynamic imports in cleanup
      // clearAllListeners is lightweight and safe to import statically
      try {
        clearAllListeners();
      } catch (error) {
        // Silently fail if there's an error (e.g., during HMR)
        // This is expected during hot module replacement
      }
    };
  }, []);

  useEffect(() => {
    refreshAOS();
    window.scrollTo(0, 0);
  }, [pathname]);

  const hideFooter = pathname.startsWith("/account") || pathname.startsWith("/admin");
  const hideNavbar = pathname.startsWith("/admin");
  const hideLoader = pathname.startsWith("/admin");
  const hidePopup = pathname.startsWith("/admin");

  // Update body background color when loader state changes
  useEffect(() => {
    // Always set to white for all pages except when loader is showing
    // Shop pages, collections, and all other pages should have white background
    if (pathname.startsWith("/admin")) {
      document.body.style.backgroundColor = "#fff";
    } else if (showLoader) {
      document.body.style.backgroundColor = "#000";
    } else {
      // All other pages (shop, collections, home, etc.) should have white background
      document.body.style.backgroundColor = "#fff";
      document.body.style.transition = "background-color 0.3s ease-in";
    }
  }, [showLoader, pathname]);

  // Show global splash loader ONLY for minimal time (non-blocking)
  // App shell should render immediately - don't wait for Firestore
  if (!appReady) {
    return (
      <ErrorBoundary>
        <GlobalAppLoader />
      </ErrorBoundary>
    );
  }

  // Always show content - don't block on Firebase data ready
  // Individual components handle their own loading/offline states
  const shouldShowContent = true;

  return (
    <ErrorBoundary>
      <AppProviders>
        {showLoader && !hideLoader && <VarahaLoading />}

        {!showLoader && !hidePopup && <PopupWrapper />}

        <Login />

        <div 
          className="app-shell" 
          aria-hidden={!shouldShowContent || showLoader ? "true" : "false"} 
          style={{ 
            opacity: shouldShowContent && !showLoader ? 1 : 0, 
            visibility: shouldShowContent && !showLoader ? 'visible' : 'hidden',
            transition: shouldShowContent && !showLoader ? 'opacity 0.3s ease-in' : 'none'
          }}
        >
          {!hideNavbar && <Navbar />}
          <main className="app-main">
            <AppContent />
          </main>
          {!hideFooter && <Footer />}
          {!hideFooter && (
            <Suspense fallback={null}>
              <LazyWhatsAppWithDelay />
            </Suspense>
          )}
        </div>
      </AppProviders>
    </ErrorBoundary>
  );
}

// Hot Module Replacement (HMR) cleanup
if (module.hot) {
  module.hot.accept();
  module.hot.dispose(() => {
    // Cleanup on hot reload to prevent HMR warnings
    // This helps with the "unexpected require from disposed module" warning
  });
}
